import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { UserGroup } from 'src/app/model/UserGroup';
import { GroupService } from 'src/app/services/group/group.service';
import { ToastrService } from 'ngx-toastr';
import { tap } from 'rxjs';
import { PermissionEntry } from 'src/app/model/User';

import { ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

class PermissionWrapper extends PermissionEntry {
  private _enabled: boolean;

  constructor(permission: string, companyId: number, enabled: boolean = false) {
    super(null, companyId, permission);
    this._enabled = enabled;
  }

  get enabled(): boolean {
    return this._enabled;
  }

  set enabled(value: boolean) {
    this._enabled = value;
  }
}

/**
 * Dialog component for creating new groups
 * Allows setting group name and feature permissions
 */
@Component({
    selector: 'app-create-group-dialog',
    imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule
],
    templateUrl: './create-group-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './create-group-dialog.component.scss'
})
export class CreateGroupDialogComponent implements OnInit {
  /** Form group for group creation */
  public groupForm!: FormGroup;

  /** New group object properties */
  public newGroup!: UserGroup;

  /** List of all permissions with their enabled state */
  public existingPermissions: PermissionWrapper[] = [];

  /** List of all existing groups for name validation */
  private _existingGroups: UserGroup[] = [];

  /** Status on if an action button was clicked */
  public doingAction: boolean = false;

  /**
   * Default constructor to initialize the dialog component
   */
  constructor(
    public dialogRef: MatDialogRef<CreateGroupDialogComponent>,
    private _formBuilder: FormBuilder,
    private _groupService: GroupService,
    private _toastr: ToastrService,
  ) { }

  /**
   * Initialize the component when the dialog is opened
   */
  ngOnInit(): void {
    // Initialize new group with empty values
    this.newGroup = new UserGroup('', '', []);

    // Load existing groups for validation
    this._existingGroups = this._groupService.getEntities() || [];

    // Load a list of all permissions
    this._groupService.getAllPermissions().pipe(tap((permissions: PermissionEntry[]) => {
      // Map permissions to PermissionWrapper with enabled state (all false initially)
      this.existingPermissions = permissions.map(p => {
        return new PermissionWrapper(p.permission, p.companyId, false);
      });
    })).subscribe();

    // Initialize group creator form with validators
    this.groupForm = this._formBuilder.group({
      name: [
        '',
        [
          Validators.required,
          this._alphanumericSpaceValidator,
          this._uniqueNameValidator.bind(this)
        ]
      ]
    });

    // Update newGroup when form value changes
    this.groupForm.get('name')?.valueChanges.subscribe(value => {
      if (value !== null && value !== undefined) {
        this.newGroup.name = value;
      }
    });
  }

  /**
   * Custom validator for alphanumeric characters and spaces only
   */
  private _alphanumericSpaceValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }

    const alphanumericSpaceRegex = /^[a-zA-Z0-9 ]+$/;
    if (!alphanumericSpaceRegex.test(control.value)) {
      return { alphanumericSpace: { value: control.value } };
    }

    return null;
  }

  /**
   * Custom validator to ensure group name is unique
   */
  private _uniqueNameValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }

    const nameExists = this._existingGroups.some(group =>
      group.name.toLowerCase() === control.value.toLowerCase()
    );

    if (nameExists) {
      return { uniqueName: { value: control.value } };
    }

    return null;
  }

  /**
   * Check if the form is valid
   */
  public isFormValid(): boolean {
    return this.groupForm.valid;
  }

  /**
   * Handle permission toggle
   */
  public onPermissionToggle(permission: PermissionWrapper): void {
    // Toggle enabled state
    this.existingPermissions.map(p => {
      if (p.permission === permission.permission) {
        p.enabled = !p.enabled;
      }
    });

    if (!permission.enabled) {
      // If we unchecked a permission, ensure it is removed from the new group
      this.newGroup.permissions = this.newGroup.permissions.filter(p => p.permission !== permission.permission);
    } else {
      // Otherwise, add it to the new group
      this.newGroup.permissions.push(new PermissionEntry(null, permission.companyId, permission.permission));
    }
  }

  /**
   * Save the new group
   */
  public save(): void {
    console.log("[CREATE-GROUP-DIALOG]: Creating new group", this.newGroup);

    // Validate form before saving
    if (!this.isFormValid()) {
      console.log("[CREATE-GROUP-DIALOG]: Form is invalid, cannot save");
      this._toastr.error('Please fix validation errors before saving');
      return;
    }

    this.doingAction = true;

    // Create the group
    this._groupService.createGroup(this.newGroup).subscribe({
      next: (g: any) => {
        console.log("[CREATE-GROUP-DIALOG]: Group created successfully", g);
        this._toastr.success('Group created successfully');
        this.doingAction = false;
        this.dialogRef.close({ action: 'save', data: g });
      },
      error: (error: any) => {
        console.error("[CREATE-GROUP-DIALOG]: Error creating group", error);
        this.doingAction = false;
        this._toastr.error('Failed to create group');
      },
    });
  }

  /**
   * Close the dialog without saving
   */
  public cancel(): void {
    this.dialogRef.close();
  }
}
